// src/pages/AdminEmployeesPanel.jsx
import React, { useEffect, useState } from 'react';
import api from '../api/api';
import './css/InventoryPage.css';
import TopNav from '../components/TopNav';
import ConfirmModal from '../components/ConfirmModal';
import AlertModal from '../components/AlertModal';

const AdminEmployeesPanel = ({ embedded = false } = {}) => {
  const [newEmployee, setNewEmployee] = useState({
    name: '',
    type: 'full_time',
    employer: '',
    hours: 40,
    days: 5,
  });

  const [employeeAddStatus, setEmployeeAddStatus] = useState('');
  const [employees, setEmployees] = useState([]);
  const [selectedEmployee, setSelectedEmployee] = useState(null);
  const [updateEmployee, setUpdateEmployee] = useState(null);
  const [filteredEmployees, setFilteredEmployees] = useState(null);

  // Optional type filter for the list
  const [typeFilter, setTypeFilter] = useState('all');

  // Search form state
  const [form, setForm] = useState({ name: '' });

  const [confirmState, setConfirmState] = useState({
    open: false,
    message: '',
    onConfirm: () => {},
  });

  const [alertState, setAlertState] = useState({
    open: false,
    message: '',
  });

  const showAlert = (message) => setAlertState({ open: true, message });

  const askConfirm = (message, onConfirm) => {
    setConfirmState({
      open: true,
      message,
      onConfirm: () => {
        onConfirm();
        setConfirmState({ open: false, message: '', onConfirm: () => {} });
      },
    });
  };

  useEffect(() => {
    document.title = "Admin Employees Panel - Services Irene's Wigs.com";
    loadEmployees();
  }, []);

  const loadEmployees = async () => {
    try {
      const res = await api.get('/employees');
      setEmployees(res.data || []);
    } catch (err) {
      console.error('Failed to load employees:', err);
      showAlert('Failed to load employees.');
    }
  };

  const handleEmployeeClick = (e, employee) => {
    // Ignore clicks starting from form controls
    if (
      e.target.tagName === 'SELECT' ||
      e.target.tagName === 'INPUT' ||
      e.target.tagName === 'TEXTAREA' ||
      e.target.closest('select') ||
      e.target.closest('input') ||
      e.target.closest('button')
    ) {
      return;
    }
    setSelectedEmployee(employee);
    setUpdateEmployee({ ...employee });
  };

  const handleSave = async () => {
    if (!updateEmployee || !selectedEmployee) return;

    try {
      const updatePayload = {
        name: updateEmployee.name?.trim() || '',
        type: updateEmployee.type,
        employer: updateEmployee.employer ?? '',
        hours: Number.isFinite(Number(updateEmployee.hours)) ? Number(updateEmployee.hours) : 0,
        days: Number.isFinite(Number(updateEmployee.days)) ? Number(updateEmployee.days) : 0,
      };

      await api.put(`/employees/${selectedEmployee.id}`, updatePayload);
      showAlert('Employee updated.');
      await loadEmployees();
    } catch (err) {
      console.error('Failed to update employee:', err);
      showAlert('Failed to update employee.');
    }
  };

  const handleAddEmployee = async (e) => {
    e.preventDefault();
    setEmployeeAddStatus('');
    try {
      const payload = {
        name: newEmployee.name?.trim(),
        type: newEmployee.type,
        employer: newEmployee.employer ?? '',
        hours: Number.isFinite(Number(newEmployee.hours)) ? Number(newEmployee.hours) : 0,
        days: Number.isFinite(Number(newEmployee.days)) ? Number(newEmployee.days) : 0,
      };

      const res = await api.post('/employees', payload);
      if (res.status === 201) {
        setEmployeeAddStatus('✅ Employee added!');
        setNewEmployee({ name: '', type: 'full_time', employer: '', hours: 40, days: 5 });
        await loadEmployees();
      } else {
        setEmployeeAddStatus(`❌ Failed: ${res.data?.error || 'Unknown error'}`);
      }
    } catch (err) {
      setEmployeeAddStatus(`❌ Error: ${err.response?.data?.error || err.message}`);
    }
  };

  const handleDelete = async (id) => {
    const employeeToDelete = employees.find((u) => u.id === id);
    if (!employeeToDelete) return;

    askConfirm('Are you sure you want to delete this employee?', async () => {
      try {
        await api.delete(`/employees/${id}`);
        await loadEmployees();
        if (selectedEmployee?.id === id) {
          setSelectedEmployee(null);
          setUpdateEmployee(null);
        }
      } catch (err) {
        console.error('Error deleting employee:', err);
        showAlert('Failed to delete employee.');
      }
    });
  };

  // Search helpers
  const handleNameChange = (e) => setForm((prev) => ({ ...prev, name: e.target.value }));

  const clearFilter = () => {
    setFilteredEmployees(null);
    setSelectedEmployee(null);
    setUpdateEmployee(null);
    setForm((prev) => ({ ...prev, name: '' }));
    setTypeFilter('all');
  };

  const reapplyFilter = (list, name) => {
    const q = (name || '').toLowerCase();
    if (!q) {
      setFilteredEmployees(list);
      return;
    }

    const exact = list.find((u) => (u.name || '').toLowerCase() === q);
    if (exact) {
      setSelectedEmployee(exact);
      setUpdateEmployee({ ...exact });
      setFilteredEmployees([exact]);
      return;
    }

    const partials = list.filter((u) => (u.name || '').toLowerCase().includes(q));
    if (partials.length === 1) {
      setSelectedEmployee(partials[0]);
      setUpdateEmployee({ ...partials[0] });
      setFilteredEmployees(partials);
      return;
    }
    if (partials.length > 1) {
      setSelectedEmployee(null);
      setUpdateEmployee(null);
      setFilteredEmployees(partials);
      return;
    }
    setSelectedEmployee(null);
    setUpdateEmployee(null);
    setFilteredEmployees([]);
    showAlert('No employee found with that name.');
  };

  const findEmployee = () => {
    const empName = form.name?.trim();
    if (!empName) return showAlert('Please enter a name to search.');
    reapplyFilter(employees, empName);
  };

  // Build the list to render with optional type filter + search results
  let listToRender = filteredEmployees === null ? employees : filteredEmployees;
  if (typeFilter !== 'all') {
    listToRender = listToRender.filter((u) => u.type === typeFilter);
  }

  return (
    <>
      {!embedded && <TopNav />}
      <div className={embedded ? 'admin-dashboard-embedded-page admin-employees-tab' : 'inventory-container'}>
        <h2>Admin Employees Panel</h2>

        {/* Add employee */}
        <form onSubmit={handleAddEmployee} className="inventory-form">
          <input
            type="text"
            placeholder="Employee Name"
            value={newEmployee.name}
            onChange={(e) => setNewEmployee({ ...newEmployee, name: e.target.value })}
            required
          />
          <select
            value={newEmployee.type}
            onChange={(e) => setNewEmployee({ ...newEmployee, type: e.target.value })}
          >
            <option value="full_time">Full Time</option>
            <option value="part_time">Part Time</option>
            <option value="1099_full_time">1099 - Full Time</option>
            <option value="1099_part_time">1099 - Part Time</option>
            <option value="sub_contractor">Sub Contractor</option>
          </select>
          <input
            type="text"
            placeholder="Employer"
            value={newEmployee.employer}
            onChange={(e) => setNewEmployee({ ...newEmployee, employer: e.target.value })}
          />
          <input
            type="number"
            placeholder="Days"
            min={0}
            max={7}
            value={newEmployee.days}
            onChange={(e) => setNewEmployee({ ...newEmployee, days: Number(e.target.value) })}
          />
          <input
            type="number"
            placeholder="Hours"
            min={0}
            value={newEmployee.hours}
            onChange={(e) => setNewEmployee({ ...newEmployee, hours: Number(e.target.value) })}
          />
          <button type="submit">Add Employee</button>
          {employeeAddStatus && <p className="status-message">{employeeAddStatus}</p>}
        </form>

        {/* Search + Filters */}
        <div className="form-group">
          <div className="input-button-group">
            <input
              type="text"
              placeholder="Search by name"
              value={form.name || ''}
              onChange={handleNameChange}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === 'Tab') {
                  e.preventDefault();
                  findEmployee();
                }
              }}
              className="input"
            />
            <button type="button" onClick={findEmployee} className="button small blue">
              Find
            </button>
            <button type="button" onClick={clearFilter} className="button small yellow">
              Clear
            </button>
          </div>
        </div>
        <div className="form-group">
          <div>
            <label style={{ display: 'block', marginBottom: 4 }}>Filter by Type:</label>
            <select value={typeFilter} onChange={(e) => setTypeFilter(e.target.value)} className="input">
              <option value="all">All</option>
              <option value="full_time">Full Time</option>
              <option value="part_time">Part Time</option>
              <option value="1099_full_time">1099 - Full Time</option>
              <option value="1099_part_time">1099 - Part Time</option>
              <option value="sub_contractor">Sub Contractor</option>
            </select>
          </div>
        </div>

        <h3>Employees</h3>

        <div className="card-list employees">
          {listToRender.length === 0 && <div className="empty-state">No employees to display.</div>}

          {listToRender.map((u) => (
            <div key={u.id} className={`card ${selectedEmployee?.id === u.id ? 'selected' : ''}`}>
              <div onClick={(e) => handleEmployeeClick(e, u)} style={{ cursor: 'pointer' }}>
                <h2>
                  {u.name} ({u.type})
                </h2>

                {selectedEmployee?.id === u.id && updateEmployee && (
                  <div className="employee-detail-panel inventory-form" onClick={(e) => e.stopPropagation()}>
                    <input
                      type="text"
                      value={updateEmployee.name || ''}
                      onChange={(e) => setUpdateEmployee({ ...updateEmployee, name: e.target.value })}
                    />

                    <input
                      type="text"
                      placeholder="Employer"
                      value={updateEmployee.employer ?? ''}
                      onChange={(e) => setUpdateEmployee({ ...updateEmployee, employer: e.target.value })}
                    />

                    <select
                      value={updateEmployee.type || 'full_time'}
                      onChange={(e) => setUpdateEmployee({ ...updateEmployee, type: e.target.value })}
                    >
                      <option value="full_time">Full Time</option>
                      <option value="part_time">Part Time</option>
                      <option value="1099_full_time">1099 - Full Time</option>
                      <option value="1099_part_time">1099 - Part Time</option>
                      <option value="sub_contractor">Sub Contractor</option>
                    </select>

                    <input
                      type="number"
                      min={0}
                      max={7}
                      value={Number.isFinite(Number(updateEmployee.days)) ? updateEmployee.days : 0}
                      onChange={(e) => setUpdateEmployee({ ...updateEmployee, days: Number(e.target.value) })}
                    />

                    <input
                      type="number"
                      min={0}
                      value={Number.isFinite(Number(updateEmployee.hours)) ? updateEmployee.hours : 0}
                      onChange={(e) => setUpdateEmployee({ ...updateEmployee, hours: Number(e.target.value) })}
                    />
                  </div>
                )}
              </div>

              {selectedEmployee?.id === u.id && (
                <div className="employee-detail-panel">
                  <div className="employee-controls">
                    <button onClick={handleSave} className="inline-button update-button">
                      Save Changes
                    </button>
                    <button onClick={() => handleDelete(selectedEmployee.id)} className="inline-button delete-button">
                      Delete
                    </button>
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
      </div>

      {alertState.open && (
        <AlertModal message={alertState.message} onClose={() => setAlertState({ open: false, message: '' })} />
      )}
      {confirmState.open && (
        <ConfirmModal
          open={confirmState.open}
          message={confirmState.message}
          onConfirm={confirmState.onConfirm}
          onCancel={() => setConfirmState({ open: false, message: '', onConfirm: () => {} })}
        />
      )}
    </>
  );
};

export default AdminEmployeesPanel;
